import { computed, DestroyRef, inject, Injectable, NgZone, Signal, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { SeasonSettingsService } from '@coin/shared/season-mgmt/data-access';
import { MeritSeasonSetting } from '@coin/shared/season-mgmt/util';
import { MeritPromotionState } from '@coin/shared/merit-incentive/util';
import { FilterSortState } from '@coin/customer/shared/filter-sort-bar-data-access';
import { PermissionsService, UserState } from '@coin/shared/auth/feature';
import { LoadingService } from '@coin/shared/core/data-access';
import { PermissionResource, SeasonProcessState, SeasonTypeEnum, TodoTaskType } from '@coin/shared/core/util-enums';
import { DateOperations } from '@coin/shared/core/util-helpers';
import { BudgetAllocationSettings, MercerData, MeritAllocationSeason, Topic } from '@coin/shared/core/util-models';
import { produce } from 'immer';
import { defer, from, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, startWith, switchMap, tap } from 'rxjs/operators';
import { TopicsState } from '../../../home/store';
import { MeritBudgetResponsibilityType } from '../../merit-shared/enums/merit-budget-responsibility-type.enum';
import { BudgetAllocationOverview } from '../../merit-shared/models/budget-allocation-overview.model';
import { EmployeeSelector } from '../../merit-shared/models/employee-selector.enum';
import { IncentiveAllocationOverview } from '../../merit-shared/models/incentive-budget-overview.model';
import { EmployeeWithCompensationInfo } from '../../merit-shared/models/merit-budget-direct-with-compensation.model';
import { MeritTask } from '../../merit-shared/models/merit-tasks.model';
import { UpdateAllocationValuesDto } from '../../merit-shared/models/update-allocation-values-dto.model';
import { MeritAllocationDirectsService } from '../../merit-shared/services/merit-allocation-directs.service';
import { MeritAllocationGeneralService } from '../../merit-shared/services/merit-allocation-general.service';
import { MeritAllocationImpersonatedEmployeeService } from '../../merit-shared/services/merit-allocation-impersonated-employee.service';
import { MeritTotalResponsibilityService } from '../../merit-shared/services/merit-total-responsibility.service';
import { IncentiveAllocationService } from '../services/incentive-allocation.service';
import { MeritTasksService } from '../services/merit-tasks.service';

interface MeritBudgetAllocationStateModel {
  allocationTask: Topic;
  isAdmin: boolean;
  tasks: { [orgCode: string]: MeritTask };
  incentiveAllocations: { [orgCode: string]: IncentiveAllocationOverview };
  season: MeritAllocationSeason;
  allocationSettings: BudgetAllocationSettings;
  seasonSettings: MeritSeasonSetting;
  seasons: MeritAllocationSeason[];
  directs: EmployeeWithCompensationInfo[];
  areDirectsLoaded: boolean;
  responsibilityType: MeritBudgetResponsibilityType;
  budgetOverview: {
    totalBudget: BudgetAllocationOverview;
    directBudget: BudgetAllocationOverview;
  };
  totalBudgetSyncDate: Date;
  onlyOpenItemsVisible: boolean;
  showMultiSelect: boolean;
  selectedEmployeeIds: string[];
  employeeSelector: EmployeeSelector;
  hasLinkedIncentiveSeason: boolean;
}

// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class MeritBudgetAllocationState {
  private readonly userState = inject(UserState);
  private readonly filterSortState = inject(FilterSortState);
  private readonly meritAllocationGeneralService = inject(MeritAllocationGeneralService);
  private readonly meritTasksService = inject(MeritTasksService);
  private readonly meritAllocationDirectsService = inject(MeritAllocationDirectsService);
  private readonly loadingService = inject(LoadingService);
  private readonly seasonSettingsService = inject(SeasonSettingsService);
  private readonly meritTotalResponsibilityService = inject(MeritTotalResponsibilityService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly incentiveAllocationService = inject(IncentiveAllocationService);
  private readonly meritAllocationImpersonatedEmployeeService = inject(MeritAllocationImpersonatedEmployeeService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionsService = inject(PermissionsService);
  private readonly topicsState = inject(TopicsState);

  // TODO(ngxs-migration): toObservable emits asynchronously, unlike store.select - verify behaviour
  private readonly currentTopicsChange$ = toObservable(this.topicsState.currentTopics);

  private readonly meritBudgetAllocationStateModel = signal<MeritBudgetAllocationStateModel>({
    allocationTask: null,
    isAdmin: false,
    tasks: {},
    incentiveAllocations: {},
    seasonSettings: undefined,
    season: undefined,
    allocationSettings: undefined,
    seasons: [],
    directs: [],
    areDirectsLoaded: false,
    responsibilityType: MeritBudgetResponsibilityType.DirectResponsibility,
    budgetOverview: {
      totalBudget: null,
      directBudget: null
    },
    totalBudgetSyncDate: null,
    onlyOpenItemsVisible: false,
    showMultiSelect: false,
    selectedEmployeeIds: [],
    employeeSelector: undefined,
    hasLinkedIncentiveSeason: false
  });

  public season = computed(() => this.meritBudgetAllocationStateModel().season);

  public isTaskDueDateReached = computed(() => {
    const state = this.meritBudgetAllocationStateModel();
    const dueDateReached = state.allocationTask?.dateEnd
      ? DateOperations.dueDateOverBasedOnDay(state.allocationTask.dateEnd)
      : !state.isAdmin;
    return dueDateReached;
  });

  public directs = computed(() => this.sortedAndFilteredDirects(this.meritBudgetAllocationStateModel()));

  public areDirectsLoaded = computed(() => this.meritBudgetAllocationStateModel().areDirectsLoaded);

  public seasons = computed(() => this.meritBudgetAllocationStateModel().seasons);
  public isAdmin = computed(() => this.meritBudgetAllocationStateModel().isAdmin);
  public allocationSettings = computed(() => this.meritBudgetAllocationStateModel().allocationSettings);
  public seasonSettings = computed(() => this.meritBudgetAllocationStateModel().seasonSettings);
  public responsibilityType = computed(() => this.meritBudgetAllocationStateModel().responsibilityType);
  public totalBudget = computed(() => this.meritBudgetAllocationStateModel().budgetOverview?.totalBudget);
  public totalBudgetSyncDate = computed(() => this.meritBudgetAllocationStateModel().totalBudgetSyncDate);
  public directBudget = computed(() => this.meritBudgetAllocationStateModel().budgetOverview?.directBudget);
  public onlyOpenItemsVisible = computed(() => this.meritBudgetAllocationStateModel().onlyOpenItemsVisible);
  public showMultiSelect = computed(() => this.meritBudgetAllocationStateModel().showMultiSelect);
  public employeeSelector = computed(() => this.meritBudgetAllocationStateModel().employeeSelector);
  public hasLinkedIncentiveSeason = computed(() => this.meritBudgetAllocationStateModel().hasLinkedIncentiveSeason);

  // for dialogs, which outlive the component that opens them and would lose a toObservable bound to its injector
  public totalBudget$ = toObservable(this.totalBudget);
  public directBudget$ = toObservable(this.directBudget);

  private sortedAndFilteredDirects(state: MeritBudgetAllocationStateModel): EmployeeWithCompensationInfo[] {
    let sortedAndFilteredDirects = state.directs;

    if (state.onlyOpenItemsVisible) {
      sortedAndFilteredDirects = sortedAndFilteredDirects?.filter(
        direct =>
          !direct.isMeritExcluded &&
          !direct.isDone &&
          ![MeritPromotionState.InProgress, MeritPromotionState.Finalized].includes(direct.promotion)
      );
    }

    return sortedAndFilteredDirects;
  }

  public taskByEmployeeOrgCode(orgCode: string): Signal<MeritTask> {
    return computed(() => this.meritBudgetAllocationStateModel().tasks?.[orgCode]);
  }

  public incentiveAllocationByEmployeeOrgCode(orgCode: string): Signal<IncentiveAllocationOverview> {
    return computed(() => this.meritBudgetAllocationStateModel().incentiveAllocations?.[orgCode]);
  }

  public isEmployeeSelected(id: string): Signal<boolean> {
    return computed(() => this.meritBudgetAllocationStateModel().selectedEmployeeIds?.includes(id));
  }

  loadDirects(): Observable<void> {
    this.loadingService.present();
    return this.meritAllocationDirectsService
      .getDirects(this.meritBudgetAllocationStateModel().season?.id, this.filterSortState.getSortAndFilterText())
      .pipe(
        tap(directs => {
          this.meritBudgetAllocationStateModel.update(state =>
            produce(state, draft => {
              draft.directs = directs;
            })
          );
        }),
        switchMap(() => this.loadMeritDirectBudgetOverview()),
        map(() => undefined),
        finalize(() => {
          this.loadingService.dismiss();
          this.meritBudgetAllocationStateModel.update(state => ({ ...state, areDirectsLoaded: true }));
        })
      );
  }

  loadDirect(employeeId: string): Observable<unknown> {
    this.loadingService.present();
    return this.meritAllocationDirectsService
      .getDirect(this.meritBudgetAllocationStateModel().season?.id, employeeId)
      .pipe(
        map(direct =>
          this.meritBudgetAllocationStateModel.update(state =>
            produce(state, draft => {
              const index = draft.directs.findIndex(stateDirect => stateDirect.id === direct.id);
              if (index > -1) {
                draft.directs[index] = { ...draft.directs[index], ...direct };
              } else {
                draft.directs = [...draft.directs, direct];
              }
            })
          )
        ),
        finalize(() => this.loadingService.dismiss())
      );
  }

  loadSeasons(): Observable<unknown> {
    return of(undefined).pipe(
      tap(() => this.setAdminRights()),
      switchMap(() => {
        if (this.meritBudgetAllocationStateModel().isAdmin) {
          return this.meritAllocationGeneralService.getMeritAllocationSeasons().pipe(
            tap(seasons => this.meritBudgetAllocationStateModel.update(state => ({ ...state, seasons }))),
            filter(seasons => seasons?.length === 1),
            switchMap(seasons => this.setSeasonInUrl(seasons[0].id))
          );
        }
        return of('').pipe(
          switchMap(() =>
            defer(() =>
              this.currentTopicsChange$.pipe(
                map(() => this.topicsState.currentTopics()),
                startWith(this.topicsState.currentTopics()),
                distinctUntilChanged()
              )
            )
          ),
          map(tasks => tasks?.filter(task => task.taskType === TodoTaskType.Allocation)),
          filter(allocationTasks => !!allocationTasks?.length),
          tap(allocationTasks =>
            this.meritBudgetAllocationStateModel.update(state => ({
              ...state,
              seasons: allocationTasks?.map(task => task.season)
            }))
          ),
          switchMap(allocationTasks => {
            const seasonsInStateBudgetAllocation = this.meritBudgetAllocationStateModel().seasons?.filter(
              season => season.state === SeasonProcessState.BudgetAllocation
            );

            if (allocationTasks.length === 1) {
              return this.setSeasonInUrl(allocationTasks[0].season.id);
            }

            if (seasonsInStateBudgetAllocation?.length === 1) {
              return this.setSeasonInUrl(seasonsInStateBudgetAllocation[seasonsInStateBudgetAllocation.length - 1].id);
            }

            return of(null);
          })
        );
      })
    );
  }

  private setSeasonInUrl(id: string): Observable<boolean> {
    return from(
      this.ngZone.run(
        function (this: Router, navigationExtras): Promise<boolean> {
          return this.navigate([], navigationExtras);
        },
        this.router,
        [{ relativeTo: this.activatedRoute, queryParams: { seasonId: id }, queryParamsHandling: 'merge' }]
      )
    );
  }

  loadAllocationSettings(seasonId: string): Observable<BudgetAllocationSettings> {
    return this.meritAllocationGeneralService.getMeritSeasonSettings(seasonId).pipe(
      tap(settings => {
        this.meritBudgetAllocationStateModel.update(state =>
          produce(state, draft => {
            draft.allocationSettings = settings;
          })
        );
      })
    );
  }

  loadSeasonSettings(payload: { id: string; type: SeasonTypeEnum }): Observable<MeritSeasonSetting> {
    return this.seasonSettingsService.getSeasonSettings(payload.id, payload.type).pipe(
      tap((settings: MeritSeasonSetting) => {
        this.meritBudgetAllocationStateModel.update(state =>
          produce(state, draft => {
            draft.seasonSettings = settings;
            draft.hasLinkedIncentiveSeason = !!settings.incentiveSeasonId;
          })
        );
      })
    );
  }

  setAdminRights(): void {
    this.meritBudgetAllocationStateModel.update(state =>
      produce(state, draft => {
        const hasAdminRights = this.permissionsService.hasPermissions(PermissionResource.PreviewAllocation);
        draft.isAdmin = hasAdminRights;
      })
    );
  }

  setResponsibilityType(responsibilityType: MeritBudgetResponsibilityType): void {
    this.meritBudgetAllocationStateModel.update(state => ({ ...state, responsibilityType }));
  }

  toggleOnlyOpenItemsVisible(): void {
    this.meritBudgetAllocationStateModel.update(state =>
      produce(state, draft => {
        draft.onlyOpenItemsVisible = !draft.onlyOpenItemsVisible;
      })
    );
  }

  toggleMultiSelect(): void {
    this.meritBudgetAllocationStateModel.update(state =>
      produce(state, draft => {
        draft.showMultiSelect = !draft.showMultiSelect;
        if (!draft.showMultiSelect) {
          draft.selectedEmployeeIds = [];
        }
      })
    );
  }

  toggleMultiSelectForEmployee(employee: EmployeeWithCompensationInfo): void {
    this.meritBudgetAllocationStateModel.update(state =>
      produce(state, draft => {
        const index = draft.selectedEmployeeIds.indexOf(employee.id);

        if (index !== -1) {
          draft.selectedEmployeeIds.splice(index, 1);
        } else {
          draft.selectedEmployeeIds.push(employee.id);
        }
      })
    );
  }

  // TODO:this functionality is available only in the design, but not yet required for development due to missing approval
  setSelectedDirectsToBudget(): void {}

  finalizeSelectedDirects(): Observable<UpdateAllocationValuesDto[]> {
    return this.updateSelectedDirects({ isDone: true });
  }

  undoSelectedDirects(): Observable<UpdateAllocationValuesDto[]> {
    return this.updateSelectedDirects({ isDone: false });
  }

  private updateSelectedDirects(
    updateBody: Partial<EmployeeWithCompensationInfo>
  ): Observable<UpdateAllocationValuesDto[]> {
    const directs = this.meritBudgetAllocationStateModel().selectedEmployeeIds.map(id =>
      this.meritBudgetAllocationStateModel().directs.find(direct => direct.id === id)
    );

    if (!directs?.length) {
      // the dispatch emitted once although the handler returned no observable
      return of(undefined);
    }

    return this.meritAllocationGeneralService
      .updateDirects(directs, updateBody, this.meritBudgetAllocationStateModel().season.id)
      .pipe(
        tap(() => {
          this.meritBudgetAllocationStateModel.update(state =>
            produce(state, draft => {
              directs.forEach(direct => {
                const foundIndex = draft.directs.findIndex(item => item.id === direct.id);

                draft.directs[foundIndex] = { ...draft.directs[foundIndex], ...{ ...direct, ...updateBody } };
              });

              draft.selectedEmployeeIds = [];
            })
          );
          this.loadMeritDirectBudgetOverview().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        })
      );
  }

  setSeason(season: MeritAllocationSeason): void {
    this.meritBudgetAllocationStateModel.update(state =>
      produce(state, draft => {
        const tasks = this.topicsState.topics();
        const allocationTasks = tasks?.filter(task => task?.taskType === TodoTaskType.Allocation);
        draft.allocationTask = allocationTasks?.find(task => task?.season?.id === season?.id);
        draft.season = season;
      })
    );
  }

  loadMeritDirectBudgetOverview(): Observable<BudgetAllocationOverview> {
    return this.meritAllocationDirectsService
      .getAllocationDirectsOverview(this.meritBudgetAllocationStateModel().season.id)
      .pipe(
        tap(overview => {
          this.meritBudgetAllocationStateModel.update(state =>
            produce(state, draft => {
              draft.budgetOverview.directBudget = overview;
            })
          );
        })
      );
  }

  setTotalBudgetOverview(orgCode: string): void {
    const user = this.userState.user();
    const state = this.meritBudgetAllocationStateModel();
    if (state && orgCode && user) {
      this.meritTotalResponsibilityService
        .getBudgetOverview(
          state.season.id,
          this.meritAllocationImpersonatedEmployeeService.employeeId || user.id,
          orgCode
        )
        .pipe(
          filter(
            totalBudget =>
              !!totalBudget &&
              !!Object.keys(totalBudget)?.length &&
              !(!totalBudget.employeeDone && !totalBudget.employeeTotal)
          )
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(totalBudget => {
          this.meritBudgetAllocationStateModel.update(currentState =>
            produce(currentState, draft => {
              draft.budgetOverview.totalBudget = new BudgetAllocationOverview(totalBudget);
              draft.totalBudgetSyncDate = new Date();
            })
          );
        });
    }
  }

  setSearchedDirect(payload: { employee: EmployeeWithCompensationInfo }): Observable<EmployeeWithCompensationInfo> {
    return this.meritAllocationDirectsService
      .getDirect(this.meritBudgetAllocationStateModel().season?.id, payload.employee.employeeId)
      .pipe(
        tap(direct => {
          this.meritBudgetAllocationStateModel.update(state => ({
            ...state,
            directs: [direct, ...state.directs.filter(alloc => alloc.id !== direct.id)]
          }));
        })
      );
  }

  updateDirect(payload: EmployeeWithCompensationInfo): void {
    this.meritBudgetAllocationStateModel.update(state =>
      produce(state, draft => {
        const foundIndex = draft.directs.findIndex(item => item.id === payload.id);

        draft.directs[foundIndex] = { ...draft.directs[foundIndex], ...payload };
      })
    );
    this.loadMeritDirectBudgetOverview().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  updateDirectCompensationComponents(payload: EmployeeWithCompensationInfo): void {
    this.meritBudgetAllocationStateModel.update(state =>
      produce(state, draft => {
        const foundIndex = draft.directs.findIndex(direct => direct.employeeId === payload.employeeId);

        if (foundIndex >= 0) {
          const direct = draft.directs[foundIndex];
          direct.compensationComponents = payload.compensationComponents ?? direct?.compensationComponents;
          direct.isDone = payload.isDone ?? direct?.isDone;
          direct.promotion = payload.promotion ?? direct?.promotion;
        }
      })
    );
  }

  loadMeritTaskByEmployeeOrgCode(employeeId: string, orgCode: string): Observable<MeritTask> {
    const state = this.meritBudgetAllocationStateModel();
    return this.meritTasksService.getMeritTaskByEmployee(state.season.id, employeeId, orgCode).pipe(
      tap(result => {
        this.meritBudgetAllocationStateModel.update(currentState =>
          produce(currentState, draft => {
            draft.tasks[orgCode] = result;
          })
        );
      })
    );
  }

  loadIncentiveTaskByEmployeeOrgCode(employeeId: string, orgCode: string): Observable<IncentiveAllocationOverview> {
    return this.loadMeritTaskByEmployeeOrgCode(employeeId, orgCode).pipe(
      switchMap(() =>
        this.incentiveAllocationService.getAllocationTotalOverview(
          this.meritBudgetAllocationStateModel().season.id,
          orgCode
        )
      ),
      tap(result => {
        this.meritBudgetAllocationStateModel.update(state =>
          produce(state, draft => {
            draft.incentiveAllocations[orgCode] = { ...result, dueDate: draft.tasks[orgCode]?.dueDate };
          })
        );
      })
    );
  }

  // the caller updates the node in MeritBudgetAllocationTotalState afterwards, which depends on this state
  changeMeritTaskStatus(payload: { seasonId: string; employee: EmployeeWithCompensationInfo }): Observable<unknown> {
    const { employee, seasonId } = payload;
    return this.meritTasksService.setTaskStatus(employee.headId ?? employee.id, seasonId);
  }

  resetMeritBudgetAllocationState(): void {
    this.meritBudgetAllocationStateModel.update(state =>
      produce(state, draft => {
        draft.allocationTask = null;
        draft.tasks = {};
        draft.incentiveAllocations = {};
        draft.seasonSettings = undefined;
        draft.season = undefined;
        draft.allocationSettings = undefined;
        draft.directs = [];
        draft.responsibilityType = MeritBudgetResponsibilityType.DirectResponsibility;
        draft.budgetOverview = {
          totalBudget: null,
          directBudget: null
        };
        draft.onlyOpenItemsVisible = false;
      })
    );
  }

  public setEmployeeSelector(employeeSelector: EmployeeSelector): void {
    this.meritBudgetAllocationStateModel.update(state =>
      produce(state, draft => {
        draft.employeeSelector = employeeSelector;
      })
    );
  }

  promoteAllocation(payload: { employeeId: string; comment?: string; mercerData?: MercerData }): Observable<void> {
    const seasonId = this.meritBudgetAllocationStateModel().season.id;

    return this.meritAllocationGeneralService
      .promoteEmployee(seasonId, payload.employeeId, { comment: payload.comment, mercerData: payload.mercerData })
      .pipe(
        tap(() => {
          this.meritBudgetAllocationStateModel.update(state =>
            produce(state, draft => {
              const index = draft.directs.findIndex(direct => direct.id === payload.employeeId);
              draft.directs[index] = { ...draft.directs[index], promotion: MeritPromotionState.Requested };
            })
          );
        })
      );
  }
}
